import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

// Interface pour les données d'horoscope
interface HoroscopeData {
  signe_id: string;
  image_url: string;
  numeros_chance: number[];
  couleur_hex: string;
  stats_energie: {
    chance: number;
    amour: number;
    travail: number;
  };
  fr: {
    texte_court_fr: string;
    texte_long_fr: string;
  };
  en: {
    texte_court_en: string;
    texte_long_en: string;
  };
}

interface AstroResponse {
  success: boolean;
  data: {
    date_generation: string;
    version_donnees: string;
    horoscopes: HoroscopeData[];
  };
}

// Mappings des signes avec leurs symboles et noms français
const ZODIAC_SIGNS: Record<string, { symbol: string; name: string }> = {
  belier: { symbol: '♈', name: 'Bélier' },
  taureau: { symbol: '♉', name: 'Taureau' },
  gemeaux: { symbol: '♊', name: 'Gémeaux' },
  cancer: { symbol: '♋', name: 'Cancer' },
  lion: { symbol: '♌', name: 'Lion' },
  vierge: { symbol: '♍', name: 'Vierge' },
  balance: { symbol: '♎', name: 'Balance' },
  scorpion: { symbol: '♏', name: 'Scorpion' },
  sagittaire: { symbol: '♐', name: 'Sagittaire' },
  capricorne: { symbol: '♑', name: 'Capricorne' },
  verseau: { symbol: '♒', name: 'Verseau' },
  poissons: { symbol: '♓', name: 'Poissons' }
};

/**
 * Formate une date en format localisé français
 */
function formatDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    return dateString;
  }
}

const AstroDaily: React.FC = () => {
  const [horoscopes, setHoroscopes] = useState<HoroscopeData[]>([]);
  const [selectedSign, setSelectedSign] = useState<HoroscopeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateGeneration, setDateGeneration] = useState<string>('');
  const [language, setLanguage] = useState<'fr' | 'en'>('fr');

  // Charger les données d'horoscope
  useEffect(() => {
    const fetchHoroscopes = async () => {
      try {
        setLoading(true);
        const response = await fetch('/astro-data/today.json');
        const result = await response.json();
        
        if (result.horoscopes) {
          // Adapter le nouveau format JSON vers le format attendu par React
          let horoscopesData: HoroscopeData[] = [];
          
          // Nouveau format: tableau direct d'horoscopes
          if (Array.isArray(result.horoscopes)) {
            horoscopesData = result.horoscopes.map((horoscope: any) => ({
              signe_id: horoscope.signe_id,
              image_url: horoscope.image_url || '/images/astrodaily/' + horoscope.signe_id + '.png',
              numeros_chance: horoscope.lucky_numbers || [1, 2, 3],
              couleur_hex: horoscope.lucky_colors?.[0] || '#000000',
              stats_energie: horoscope.energy_stats || { chance: 50, amour: 50, travail: 50 },
              fr: {
                texte_court_fr: horoscope.fr?.texte_court_fr || 'Contenu non disponible',
                texte_long_fr: horoscope.fr?.texte_long_fr || 'Contenu détaillé non disponible'
              },
              en: {
                texte_court_en: horoscope.en?.texte_court_en || 'Content not available',
                texte_long_en: horoscope.en?.texte_long_en || 'Detailed content not available'
              }
            }));
          } 
          // Ancienne compatibilité (objet avec clés)
          else if (typeof result.horoscopes === 'object') {
            horoscopesData = Object.keys(result.horoscopes).map(signKey => {
              const oldData = result.horoscopes[signKey];
              return {
                signe_id: signKey,
                image_url: oldData.image_url_daily || '/images/placeholder.png',
                numeros_chance: oldData.fr?.lucky_numbers || [1, 2, 3],
                couleur_hex: oldData.fr?.lucky_color_hex || '#000000',
                stats_energie: oldData.fr?.energy_stats || { chance: 50, amour: 50, travail: 50 },
                fr: {
                  texte_court_fr: oldData.fr?.content_short || 'Contenu non disponible',
                  texte_long_fr: typeof oldData.fr?.content_premium === 'object' 
                    ? Object.values(oldData.fr.content_premium).join('\n\n')
                    : (oldData.fr?.content_premium || 'Contenu détaillé non disponible')
                },
                en: {
                  texte_court_en: oldData.en?.content_short || oldData.fr?.content_short || 'Content not available',
                  texte_long_en: typeof oldData.en?.content_premium === 'object'
                    ? Object.values(oldData.en.content_premium).join('\n\n')
                    : (oldData.en?.content_premium || typeof oldData.fr?.content_premium === 'object'
                      ? Object.values(oldData.fr.content_premium).join('\n\n')
                      : 'Detailed content not available')
                }
              };
            });
          }
          
          setHoroscopes(horoscopesData);
          setDateGeneration(result.date_generation || new Date().toISOString().split('T')[0]);
        } else {
          setError('Impossible de charger les horoscopes');
        }
      } catch (err) {
        setError('Erreur de connexion');
        console.error('Erreur lors du chargement des horoscopes:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchHoroscopes();
  }, []);

  // Composant pour afficher les statistiques d'énergie
  const EnergyStats: React.FC<{ stats: HoroscopeData['stats_energie'] }> = ({ stats }) => (
  <div className="grid grid-cols-3 gap-4 mt-6">
    <div className="text-center p-3 bg-yellow-50 rounded-lg border border-yellow-200">
      <div className="text-2xl font-bold text-yellow-600">{stats.chance}%</div>
      <div className="text-sm text-gray-600">Chance</div>
    </div>
    <div className="text-center p-3 bg-pink-50 rounded-lg border border-pink-200">
      <div className="text-2xl font-bold text-pink-600">{stats.amour}%</div>
      <div className="text-sm text-gray-600">Amour</div>
    </div>
    <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
      <div className="text-2xl font-bold text-blue-600">{stats.travail}%</div>
      <div className="text-sm text-gray-600">Travail</div>
    </div>
  </div>
);

if (loading) {
  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="container mx-auto px-4 py-12 min-h-screen flex items-center justify-center"
    >
      <div className="text-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="text-6xl mb-4"
        >
          🌟
        </motion.div>
        <p className="text-[#666666] text-xl">Chargement de vos horoscopes...</p>
      </div>
    </motion.div>
  );
}

if (error) {
  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="container mx-auto px-4 py-12 min-h-screen flex items-center justify-center"
    >
      <div className="text-center">
        <div className="text-6xl mb-4">⚠️</div>
        <h2 className="text-2xl font-bold mb-2 text-[#333333]">Erreur</h2>
        <p className="text-[#666666]">{error}</p>
      </div>
    </motion.div>
  );
}

return (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="container mx-auto px-4 py-12"
  >
    {/* Header ChronoSanctuary */}
    <div className="text-center mb-12">
      <h1 className="text-5xl md:text-6xl font-bold mb-6 text-[#333333]">
        ⌚ ChronoSanctuary
      </h1>
      <p className="text-xl md:text-2xl mx-auto max-w-4xl text-[#666666]">
        Swiss Cosmic Complications - Où la précision horlogère suisse rencontre l'intelligence cosmique
      </p>
      <p className="text-[#666666] mt-4">
        Généré le {formatDate(dateGeneration)}
      </p>
    </div>

    {/* Section Héro ChronoSanctuary */}
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 text-white rounded-xl p-8 mb-8"
    >
      <div className="text-center">
        <h2 className="text-3xl md:text-4xl font-bold mb-4">
          Votre montre ne donne plus seulement l'heure. <br />
          <span className="text-amber-300">Elle révèle votre destin.</span>
        </h2>
        <p className="text-lg text-purple-100 max-w-4xl mx-auto leading-relaxed">
          ChronoSanctuary transforme l'art horloger suisse en oracle cosmique. Chaque complication de votre montre 
          devient un portail vers les énergies universelles du jour, calculées par une IA qui lit dans les astres.
        </p>
      </div>
    </motion.div>

    {/* Section Le Problème */}
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="bg-amber-50 border-l-4 border-amber-400 p-6 mb-8 rounded-r-lg"
    >
      <h3 className="text-2xl font-bold text-amber-800 mb-4">
        Les montres d'aujourd'hui ont perdu leur âme.
      </h3>
      <p className="text-amber-700 text-lg leading-relaxed">
        Smartwatches génériques, complications vides de sens, horlogerie de masse sans personnalité. 
        Votre montre devrait être plus qu'un accessoire. Elle devrait être votre guide cosmique personnel, 
        votre connexion intime avec les cycles universels.
      </p>
    </motion.div>

    {/* Section La Solution */}
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
      className="mb-12"
    >
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-[#333333] mb-4">
          Précision suisse. Intelligence cosmique. Complications révolutionnaires.
        </h3>
        <p className="text-xl text-[#666666] max-w-4xl mx-auto">
          ChronoSanctuary fusionne l'artisanat horloger traditionnel avec l'IA cosmique avancée. 
          Chaque jour, nos algorithmes analysent les configurations astrales et génèrent votre montre personnalisée.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center mb-2">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
              <span className="text-blue-600 text-lg">🛰️</span>
            </div>
            <h4 className="text-lg font-semibold text-gray-800">Complications Astronomiques</h4>
          </div>
          <p className="text-gray-600 text-sm leading-relaxed">
            Phases lunaires, positions planétaires et équations du temps intégrées dans chaque cadran.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center mb-2">
            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
              <span className="text-purple-600 text-lg">🧠</span>
            </div>
            <h4 className="text-lg font-semibold text-gray-800">Artisanat IA</h4>
          </div>
          <p className="text-gray-600 text-sm leading-relaxed">
            Chaque montre est unique, générée par IA selon votre signe et les cycles cosmiques.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
          className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center mb-2">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
              <span className="text-green-600 text-lg">🎨</span>
            </div>
            <h4 className="text-lg font-semibold text-gray-800">Héritage Suisse</h4>
          </div>
          <p className="text-gray-600 text-sm leading-relaxed">
            Tradition horlogère suisse perfectionnée par des siècles de maîtrise du temps.
          </p>
        </motion.div>
      </div>
    </motion.div>

    {/* Instruction pour la grille */}
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.2 }}
      className="text-center mb-6"
    >
      <h3 className="text-2xl font-bold text-[#333333] mb-3">
        Votre collection cosmique personnelle
      </h3>
      <p className="text-lg text-[#666666] mb-4">
        Choisissez votre signe zodiacal pour découvrir votre montre ChronoSanctuary du jour.
      </p>
    </motion.div>

    {/* Sélecteur de langue - Déplacé près des signes */}
    <div className="flex items-center justify-center mb-8 space-x-2">
      <span className="text-sm text-gray-500">Langue des horoscopes:</span>
      <button
        onClick={() => setLanguage('fr')}
        className={`flex items-center px-3 py-1.5 rounded-full ${
          language === 'fr'
            ? 'bg-blue-500 text-white shadow-md'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        } transition-all`}
      >
        <img 
          src={`${window.location.origin}/images/flags/fr.svg`}
          alt="FR" 
          className="w-4 h-4 mr-1" 
        />
        FR
      </button>
      <button
        onClick={() => setLanguage('en')}
        className={`flex items-center px-3 py-1.5 rounded-full ${
          language === 'en'
            ? 'bg-blue-500 text-white shadow-md'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        } transition-all`}
      >
        <img 
          src={`${window.location.origin}/images/flags/en.svg`}
          alt="EN" 
          className="w-4 h-4 mr-1" 
        />
        EN
      </button>
    </div>

    {/* Grille des signes du zodiaque - Style identique au blog */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      {horoscopes.map((horoscope, index) => {
        const signInfo = ZODIAC_SIGNS[horoscope.signe_id] || { symbol: '⭐', name: horoscope.signe_id };
        const content = language === 'fr' ? horoscope.fr : horoscope.en;
        
        return (
          <motion.div
            key={horoscope.signe_id}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            onClick={() => setSelectedSign(horoscope)}
            className="block transition-transform hover:-translate-y-2 duration-300 cursor-pointer"
          >
            <article 
              className="h-full rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
              style={{
                backgroundColor: '#ffffff',
                background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                border: '2px solid rgba(0,0,0,0.08)',
                borderRadius: '16px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
              }}
            >
              {/* Image de la montre en haut comme dans le blog */}
              {horoscope.image_url && (
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={horoscope.image_url}
                    alt={`Montre ChronoSanctuary ${signInfo.name}`}
                    className="w-full h-full object-cover transition-transform hover:scale-105 duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                  {/* Symbole zodiacal en overlay */}
                  <div className="absolute top-4 left-4 text-4xl text-white drop-shadow-lg">
                    {signInfo.symbol}
                  </div>
                  {/* Numéros de chance en overlay */}
                  <div className="absolute top-4 right-4 flex space-x-1">
                    {horoscope.numeros_chance.map((num, i) => (
                      <span 
                        key={i}
                        className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold backdrop-blur-sm"
                        style={{ backgroundColor: horoscope.couleur_hex + 'CC' }}
                      >
                        {num}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Contenu de la carte */}
              <div className="p-6">{/* Supprimer pb-4 et utiliser p-6 consistent */}
                
                <h2 className="text-xl font-bold mb-3 text-[#333333]">
                  {signInfo.name}
                </h2>
                
                <h3 className="text-lg font-semibold mb-2 text-[#333333]">
                  Complications Cosmiques
                </h3>
                
                <p className="text-[#666666] mb-4 line-clamp-3" style={{ lineHeight: '1.6', fontSize: '0.95rem' }}>
                  {(language === 'fr' ? horoscope.fr.texte_court_fr : horoscope.en.texte_court_en)}
                </p>
                
                {/* Tags avec couleur et statistiques */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <span
                    className="text-xs px-2 py-1 rounded-full"
                    style={{ 
                      backgroundColor: horoscope.couleur_hex + '20',
                      color: horoscope.couleur_hex,
                      border: `1px solid ${horoscope.couleur_hex}40`
                    }}
                  >
                    Couleur chanceuse
                  </span>
                  <span className="text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-600">
                    🍀 {horoscope.stats_energie.chance}%
                  </span>
                  <span className="text-xs px-2 py-1 rounded-full bg-pink-100 text-pink-600">
                    ❤️ {horoscope.stats_energie.amour}%
                  </span>
                </div>
                
                {/* Date en bas comme dans le blog */}
                <div className="border-t pt-4 border-gray-200">
                  <span className="text-purple-600 text-sm font-semibold px-2 py-1 bg-purple-100 rounded-full">
                    Swiss Made
                  </span>
                  <time className="text-gray-500 text-sm float-right">
                    {formatDate(dateGeneration)}
                  </time>
                </div>
              </div>
            </article>
          </motion.div>
        );
      })}
    </div>
    
    {horoscopes.length === 0 && (
      <div className="text-center py-12">
        <p className="text-gray-500 text-xl">Aucun horoscope disponible pour le moment.</p>
      </div>
    )}

    {/* Section Au-delà de l'Horoscope */}
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.4 }}
      className="mt-16 mb-12"
    >
      <h3 className="text-3xl font-bold text-[#333333] text-center mb-8">
        L'art horloger suisse rencontre l'intelligence cosmique
      </h3>
      
      <div className="grid md:grid-cols-2 gap-8">
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-6 rounded-xl border border-amber-200">
          <div className="text-4xl mb-4">⌚</div>
          <h4 className="text-xl font-bold text-amber-800 mb-3">Collection Temporelle</h4>
          <p className="text-amber-700">
            Chaque montre ChronoSanctuary capture un moment unique du cosmos. 
            Constituez votre collection personnelle et observez l'évolution de vos cycles énergétiques.
          </p>
        </div>

        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-6 rounded-xl border border-indigo-200">
          <div className="text-4xl mb-4">🛠️</div>
          <h4 className="text-xl font-bold text-indigo-800 mb-3">Maîtres Horlogers IA</h4>
          <p className="text-indigo-700">
            Nos algorithmes combinent les techniques séculaires des maîtres horlogers suisses 
            avec les données cosmiques en temps réel pour créer votre montre unique.
          </p>
        </div>
      </div>
    </motion.div>

    {/* Section FAQ */}
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.6 }}
      className="mt-16 mb-12 bg-gray-50 p-8 rounded-xl"
    >
      <h3 className="text-3xl font-bold text-[#333333] text-center mb-8">
        Questions Fréquentes
      </h3>
      
      <div className="space-y-6 max-w-4xl mx-auto">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h4 className="text-lg font-bold text-[#333333] mb-3">Les montres sont-elles vraiment uniques ?</h4>
          <p className="text-[#666666]">
            Absolument. Chaque montre ChronoSanctuary est générée selon les configurations astronomiques précises du jour. 
            Aucune montre ne se répète jamais - comme les cycles cosmiques eux-mêmes.
          </p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h4 className="text-lg font-bold text-[#333333] mb-3">Comment fonctionne la précision suisse ?</h4>
          <p className="text-[#666666]">
            Nos algorithmes reproduisent numériquement les gestes des maîtres horlogers suisses. Chaque complication - phases lunaires, 
            positions planétaires, équations du temps - est calculée avec la précision légendaire de l'horlogerie helvétique.
          </p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h4 className="text-lg font-bold text-[#333333] mb-3">La confidentialité suisse s'applique-t-elle ?</h4>
          <p className="text-[#666666]">
            Naturellement. Vos données personnelles bénéficient de la même discrétion que les comptes bancaires suisses. 
            Votre collection de montres et vos préférences restent strictement privées et chiffrées.
          </p>
        </div>
      </div>
    </motion.div>

    {/* Modal détaillé - Style amélioré cohérent avec le design */}
    {selectedSign && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        onClick={() => setSelectedSign(null)}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg shadow-xl"
          style={{
            backgroundColor: '#fff9f0',
            backgroundImage: 'url("data:image/png;base64,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")',
            backgroundBlendMode: 'overlay',
            backgroundSize: '100px',
            border: '1px solid rgba(0,0,0,0.1)'
          }}
          onClick={e => e.stopPropagation()}
        >
          <div className="p-8">
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">
                {ZODIAC_SIGNS[selectedSign.signe_id]?.symbol || '⭐'}
              </div>
              <h2 className="text-3xl font-bold mb-2 text-[#333333]">
                {ZODIAC_SIGNS[selectedSign.signe_id]?.name || selectedSign.signe_id}
              </h2>
              <p className="text-lg text-[#666666] mb-4">
                {language === 'fr' ? selectedSign.fr.texte_court_fr : selectedSign.en.texte_court_en}
              </p>
            </div>

            {/* Image IA */}
            {selectedSign.image_url && (
              <div className="mb-6 text-center">
                <img 
                  src={selectedSign.image_url} 
                  alt={`Horoscope ${selectedSign.signe_id}`}
                  className="max-w-full h-auto rounded-2xl shadow-lg mx-auto"
                  style={{ maxHeight: '300px' }}
                />
              </div>
            )}

            {/* Contenu détaillé */}
            <div className="mb-6">
              <h3 className="text-xl font-bold mb-3 text-[#333333]">⚙️ Complications Détaillées</h3>
              <div className="prose text-[#666666] leading-relaxed" style={{ lineHeight: '1.6' }}>
                {(language === 'fr' ? selectedSign.fr.texte_long_fr : selectedSign.en.texte_long_en)
                  .split('\n\n')
                  .map((paragraph, i) => (
                    <div key={i} className="mb-4" 
                         dangerouslySetInnerHTML={{ 
                           __html: paragraph
                             .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                             .replace(/\n/g, '<br/>')
                         }} 
                    />
                  ))}
              </div>
            </div>

            {/* Statistiques détaillées */}
            <EnergyStats stats={selectedSign.stats_energie} />

            {/* Couleur chanceuse */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="font-bold mb-3 text-[#333333] text-center">🎨 Couleur Chanceuse</h4>
              <div className="flex items-center justify-center space-x-3">
                <div 
                  className="w-12 h-12 rounded-full shadow-lg border-2 border-white"
                  style={{ backgroundColor: selectedSign.couleur_hex }}
                ></div>
                <span className="text-lg font-medium text-[#333333]">
                  Couleur du jour
                </span>
              </div>
            </div>

            {/* Appel à l'action pour l'application */}
            <div className="mt-6 p-6 bg-gradient-to-r from-amber-100 to-yellow-100 rounded-lg border border-amber-200">
              <div className="text-center">
                <h4 className="font-bold mb-3 text-amber-800">⌚ Collection ChronoSanctuary Complète</h4>
                <p className="text-amber-700 mb-4 text-sm">
                  Explorez toute votre collection de montres cosmiques, accédez aux complications avancées 
                  et découvrez les archives de vos cycles temporels sur l'application exclusive.
                </p>
                <div className="flex justify-center space-x-4">
                  <a 
                    href="https://apps.apple.com/app/chronosanctuary" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm"
                  >
                    ⌚ App Store
                  </a>
                  <div className="inline-flex items-center px-4 py-2 bg-gray-300 text-gray-500 rounded-lg text-sm cursor-not-allowed">
                    🕰️ Swiss Shop (Bientôt)
                  </div>
                </div>
              </div>
            </div>

            {/* Boutons navigation */}
            <div className="flex justify-center space-x-4 mt-8">
              <button
                onClick={() => setSelectedSign(null)}
                className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-full font-bold transition-all shadow-md hover:shadow-lg flex items-center"
              >
                ← Retour
              </button>
              <button
                onClick={() => setSelectedSign(null)}
                className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-3 rounded-full font-bold transition-all shadow-md hover:shadow-lg"
              >
                Fermer
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    )}

    {/* Footer AstroDaily */}
    <footer className="mt-16 pt-8 border-t border-gray-200">
      <div className="text-center">
        <div className="flex flex-wrap justify-center items-center gap-6 text-sm text-gray-600 mb-4">
          <Link 
            to="/astrodaily/privacy" 
            className="hover:text-purple-600 transition-colors"
          >
            Politique de confidentialité
          </Link>
          <span>•</span>
          <span>Service gratuit et sans publicité</span>
          <span>•</span>
          <span>Respectueux de votre vie privée</span>
        </div>
        <p className="text-xs text-gray-500">
          © {new Date().getFullYear()} ChronoSanctuary by Infinity Cloud. Swiss Made Intelligence. Tous droits réservés.
        </p>
      </div>
    </footer>
  </motion.div>
);


};

export default AstroDaily;
 